import { MapPin, Phone, Mail, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Reveal, BookButton } from "@/components/bits";
import { CLINIC, CONTACT, HOURS } from "@/lib/site";

const telHref = (p) => `tel:${p.replace(/\s/g, "")}`;

const ROWS = [
  {
    icon: MapPin,
    label: "Besøksadresse",
    value: (
      <>
        {CLINIC.name}
        <br />
        {CLINIC.address}, 7. etasje
        <br />
        {CLINIC.city}
      </>
    ),
    testId: "contact-address",
  },
  {
    icon: Phone,
    label: "Telefon",
    value: (
      <span className="flex flex-col gap-2">
        {CONTACT.phones.map((p) => (
          <span key={p.number} className="grid grid-cols-[8.5rem_auto] gap-4 items-baseline">
            <span>{p.name}</span>
            <a href={telHref(p.number)} className="hover:text-ink transition-colors duration-300">
              {p.number}
            </a>
          </span>
        ))}
      </span>
    ),
    testId: "contact-phone",
  },
  {
    icon: Mail,
    label: "E-post",
    value: (
      <a href={`mailto:${CONTACT.email}`} className="hover:text-ink transition-colors duration-300">
        {CONTACT.email}
      </a>
    ),
    testId: "contact-email",
  },
];

export default function Kontakt() {
  return (
    <>
      <Seo
        title="Kontakt oss | Actus Osteopati – Lilletorget 1, Oslo"
        description="Kontakt Actus Osteopati på Lilletorget 1, 0184 Oslo. Book time hos osteopat i Oslo sentrum."
      />
      <PageHeader
        overline="Kontakt"
        title="Kontakt oss"
        testId="kontakt-heading"
      />

      <section data-testid="kontakt-content" className="bg-paper py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              {ROWS.map((r, i) => (
                <Reveal key={r.label} delay={i * 0.08}>
                  <div data-testid={r.testId} className="flex gap-6 py-8 border-t border-ink/10">
                    <r.icon size={20} className="mt-1 shrink-0 text-sage-deep" />
                    <div>
                      <p className="text-xs tracking-[0.3em] uppercase text-ink-soft/60 mb-2">{r.label}</p>
                      <p className="text-base md:text-lg leading-relaxed text-ink-soft">{r.value}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.35}>
                <div className="mt-10">
                  <BookButton testId="kontakt-book-button" dark label="Book time nå" />
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.15}>
              <div data-testid="kontakt-map" className="h-full min-h-[420px] border border-ink/10 overflow-hidden">
                <iframe
                  title="Kart over Actus Osteopati, Lilletorget 1, Oslo"
                  src="https://www.google.com/maps?q=Lilletorget%201,%200184%20Oslo,%20Norge&output=embed"
                  className="w-full h-full min-h-[420px]"
                  style={{ border: 0, filter: "grayscale(35%) sepia(12%)" }}
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
          <Reveal>
            <div data-testid="kontakt-hours" className="mt-20 pt-14 border-t border-ink/10 text-center max-w-xl mx-auto">
              <p className="text-sm tracking-[0.3em] uppercase text-ink-soft/60 mb-8">Åpningstider</p>
              <div className="flex flex-col gap-2">
                {HOURS.map(([day, time]) => (
                  <div key={day} className="flex justify-center gap-10">
                    <span className="w-28 text-left text-ink-soft">{day}</span>
                    <span className="text-ink-soft">{time}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
